<script setup lang="ts">
import { computed, markRaw, onMounted, reactive, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useDisplay } from 'vuetify';

import {
  formatCurrency,
  formatDate,
  formatItemValue,
  formatPercentage,
} from '@/utils/formatters.ts';
import MainTitleComponent from "@/components/ui/MainTitleComponent.vue";
import DividerComponent from "@/components/ui/DividerComponent.vue";
import validators from "@/utils/validators.ts";
import TradingMandateService from "@/services/mandate/tradingMandateService.ts";
import { displayModal } from "@/utils/modalUtils.ts";
import SelectAssetComponent from "@/components/mandate/SelectAssetComponent.vue";
import { TradingOrderTypeEnum } from "@/enums/mandate_enum.ts";
import { displayToast } from "@/utils/toastUtils.ts";
import { useAuthStore } from "@/stores/authStore.ts";

import type {
  DataTableHeader,
  ValidationRule
} from "vuetify/framework";
import type {
  AssetDetailResponse,
  MandateTradingDataResponseApi,
  OrderForm,
  OrderHistoryRow,
  OrdersHistorySortKey
} from "@/types/services/mandate/tradingMandate.type.ts";
import type { VForm } from "vuetify/components";
import type { PaginatedNoSearchQuery, SortItem, SortOrder } from "@/types/services/utils/request.type.ts";
import type { PaginatedResponse } from "@/types/services/utils/response.type.ts";

const { t } = useI18n();
const route = useRoute();
const { smAndDown } = useDisplay();
const authStore = useAuthStore()

const step = ref<number>(1);
const loading = ref<boolean>(false);

const mandateId: number = route.meta.mandateId as number;
const tradingData = ref<MandateTradingDataResponseApi>(route.meta.tradingData as MandateTradingDataResponseApi);
const cryptoAssets = computed(() =>
  tradingData.value.assets.filter(asset => asset.name != "EURC")
);

//--- Trading order form ---//
const orderFormRef = ref<VForm | null>(null);
const orderFormValid = ref<boolean>(false);
const orderAmountRules = ref<ValidationRule[]>([...validators.orderAmountRules]);
const orderLimitPriceRules = ref<ValidationRule[]>([...validators.orderLimitPriceRules]);
const orderForm = reactive<OrderForm>({
  orderType: TradingOrderTypeEnum.MARKET_ORDER,
  fromAsset: '',
  amount: '',
  toAsset: '',
  limitPrice: '',
})

function clearOrderForms() {
  orderForm.orderType = TradingOrderTypeEnum.MARKET_ORDER;
  orderForm.fromAsset = '';
  orderForm.amount = '';
  orderForm.toAsset = '';
  orderForm.limitPrice = '';
  orderFormRef.value?.resetValidation();
}

// --- Orders history table ---//
const ordersHistory = ref<OrderHistoryRow[]>([]);
const sort = ref<OrdersHistorySortKey>("time");
const order = ref<SortOrder>("desc");
const limit = ref<number>(10);
const page = ref<number>(1);
const total = ref<number>(0);
const maxPages = ref<number>(1);
const headers = computed<DataTableHeader[]>(() => [
  {
    title: t("views.tradingMandate.table.asset.assetName"),
    key: "assetName",
    align: "start",
    sortable: false
  },
  {
    title: t("views.tradingMandate.table.asset.side"),
    key: "side",
    align: "start",
    sortable: true
  },
  {
    title: t("views.tradingMandate.table.asset.type"),
    key: "subType",
    align: "start",
    sortable: true
  },
  {
    title: t("views.tradingMandate.table.asset.total"),
    key: "total",
    align: "start",
    sortable: true
  },
  {
    title: t("views.tradingMandate.table.asset.requestedPrice"),
    key: "requestedPrice",
    align: "start",
    sortable: true
  },
  {
    title: t("views.tradingMandate.table.asset.status"),
    key: "status",
    align: "start",
    sortable: true
  },
  {
    title: t("views.tradingMandate.table.asset.startDate"),
    key: "time",
    align: "start",
    sortable: true
  },
  {
    title: t("views.tradingMandate.table.asset.closeDate"),
    key: "closingTime",
    align: "start",
    sortable: true
  },
  {
    title: t("views.tradingMandate.table.asset.action"),
    key: "action",
    align: "end",
    sortable: true
  }
]);

onMounted(fetchOrdersHistory);

watch([sort, order, limit, page], fetchOrdersHistory);

function availableFromAssets() {
  const used = orderForm.toAsset ? [orderForm.toAsset] : [];
  return tradingData.value.assets.filter(asset => !used.includes(asset.name) || asset.name === orderForm.fromAsset);
}

function availableToAssets() {
  const used = orderForm.fromAsset ? [orderForm.fromAsset] : [];
  return tradingData.value.assets.filter(asset => !used.includes(asset.name) || asset.name === orderForm.toAsset);
}

async function selectAsset(from: boolean = true): Promise<void> {
  const modal = await displayModal({
    component: markRaw(SelectAssetComponent),
    props: {
      availableAssets: from
        ? availableFromAssets()
        : availableToAssets(),
    },
  })

  if (modal && typeof modal === 'object') {
    const { name } = modal as { name: string };
    if (from) {
      orderForm.fromAsset = name;
    } else {
      orderForm.toAsset = name;
    }
  }
}

function getAmountAvailableByAsset(assetName: string) {
  return computed(() => {
    const asset = tradingData.value.assets.find(a => a.name === assetName);
    return asset ? asset.amount : 0;
  });
}

function gapColor(asset: AssetDetailResponse) {
  const diff = Math.abs(asset.percentage - asset.mandatePercentage);
  if (diff < 2) return "green";
  if (diff < 5) return "orange";
  return "red";
}

function swapAssets() {
  const tempAsset = orderForm.fromAsset;
  orderForm.fromAsset = orderForm.toAsset;
  orderForm.toAsset = tempAsset;
}

async function placeOrder() {
  try {
    loading.value = true;
    let response: MandateTradingDataResponseApi | null = null;

    switch (orderForm.orderType) {
      case TradingOrderTypeEnum.MARKET_ORDER:
        response = await TradingMandateService.placeMarketOrder(
          mandateId,
          orderForm.fromAsset,
          parseFloat(orderForm.amount),
          orderForm.toAsset,
        );
        break;
      case TradingOrderTypeEnum.LIMIT_ORDER:
        response = await TradingMandateService.placeLimitOrder(
          mandateId,
          orderForm.fromAsset,
          parseFloat(orderForm.amount),
          orderForm.toAsset,
          parseFloat(orderForm.limitPrice),
        );
        break;
    }

    if (response) {
      tradingData.value = response;
      displayToast(t('views.mandatesList.tradingMandate.placeOrderSuccess'), 'success')
      clearOrderForms();
      await fetchOrdersHistory();
    }
  } finally {
    loading.value = false;
  }
}

function onSort(sortBy: SortItem<OrdersHistorySortKey>[]): void {
  const first = sortBy?.[0];
  sort.value = first.key;
  order.value = first.order;
}

async function fetchOrdersHistory() {
  try {
    loading.value = true;

    const params: PaginatedNoSearchQuery = {
      sort: sort.value,
      order: order.value,
      limit: limit.value,
      page: page.value
    };

    const response: PaginatedResponse<OrderHistoryRow> = await TradingMandateService.getPaginatedTradingHistory(
      mandateId,
      params,
    );
    ordersHistory.value = response.data;
    total.value = response.total;
    maxPages.value = response.maxPages;
  } finally {
    loading.value = false;
  }
}

function isOrderOngoing(orderStatus: string): boolean {
  return orderStatus === "open";
}

function hasPermission(permission: string[]): boolean {
  return authStore.hasPermission(permission);
}

async function cancelOrder(order: OrderHistoryRow) {
  try {
    loading.value = true;

    await TradingMandateService.cancel_order(
      mandateId,
      order.orderId,
    );

    await fetchOrdersHistory();
    displayToast(t('views.mandatesList.tradingMandate.cancelOrderSuccess'), 'success');
  } finally {
    loading.value = false;
  }
}
</script>

<template>
  <!-- Main title -->
  <MainTitleComponent>
    <div class="d-flex flex-column">
      {{ t("views.tradingMandate.title") }}
      <span class="text-subtitle-1">{{ tradingData.mandateName }}</span>
    </div>
  </MainTitleComponent>

  <v-stepper v-model="step" :elevation="0" :mobile="smAndDown">
    <v-stepper-header>
      <v-divider />

      <v-stepper-item
        :title="t('views.tradingMandate.stepper.step1')"
        :value="1"
        color="primary"
        editable
        edit-icon=""
      />

      <v-divider />

      <v-stepper-item
        :title="t('views.tradingMandate.stepper.step2')"
        :value="2"
        color="primary"
        editable
        edit-icon=""
      />

      <v-divider />
    </v-stepper-header>

    <v-stepper-window>
      <!-- Step 1: Cryptocurrency trade -->
      <v-stepper-window-item :value="1">
        <!-- Assets Distribution Section -->
        <v-row align="center" class="d-flex align-center" justify="space-between">
          <v-col class="d-flex align-center" cols="12">
            <v-icon class="mr-2" color="primary" icon="PhChartPie"/>
            <h2 class="subtitle">{{ t("views.tradingMandate.assetDistribution") }}</h2>
          </v-col>
          <v-col cols="12">
            <v-row v-if="cryptoAssets.length !== 0">
              <v-col
                v-for="asset in cryptoAssets"
                :key="asset.name"
                cols="12"
                md="4"
              >
                <v-alert
                  color="grey-lighten-3"
                  variant="outlined"
                >
                  <div class="d-flex justify-space-between align-center mb-3">
                    <div class="d-flex align-center text-black">
                      {{ asset.name }}
                    </div>
                    <v-chip
                      :color="gapColor(asset)"
                      class="text-white"
                      size="small"
                    >
                      {{ formatPercentage(asset.percentage) }}%
                    </v-chip>
                  </div>
                  <v-progress-linear
                    :model-value="Number(asset.percentage)"
                    height="10"
                    color="primary"
                    rounded
                  />
                  <div :class="smAndDown ? 'flex-column' : 'justify-space-between'" class="mt-3 d-flex text-caption">
                    <span class="text-grey-darken-1">
                      {{ t("views.tradingMandate.target") }}:
                      <strong>
                        {{ formatPercentage(asset.mandatePercentage) }}%
                      </strong>
                    </span>
                    <span class="text-grey-darken-1">
                      {{ t("views.tradingMandate.current_rate") }}:
                      <strong>
                        {{ formatPercentage(asset.currentRate) }}€
                      </strong>
                    </span>
                  </div>
                </v-alert>
              </v-col>
            </v-row>

            <v-row v-else>
              <v-col cols="12">
                <v-alert
                  color="grey-lighten-3"
                  variant="outlined"
                >
                  <span class="text-black">{{ t("views.tradingMandate.noCryptoAssets") }}</span>
                </v-alert>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <DividerComponent />

        <!-- Trade Order Section -->
        <v-row align="center" class="d-flex align-center" justify="space-between">
          <v-col class="d-flex align-center" cols="12">
            <v-icon class="mr-2" color="primary" icon="PhArrowsClockwise"/>
            <h2 class="subtitle">{{ t("views.tradingMandate.placeOrder") }}</h2>
          </v-col>
          <v-col cols="12">
            <v-alert
              color="grey-lighten-3"
              variant="outlined"
            >
              <v-form
                ref="orderFormRef"
                v-model="orderFormValid"
                @submit.prevent="placeOrder"
              >
                <v-row align="baseline" class="mt-1" justify="space-between">
                  <!-- Order Type Select -->
                  <v-col cols="12" md="auto" style="width: 45%;">
                    <v-select
                      v-model="orderForm.orderType"
                      :disabled="loading"
                      :items="[
                        { title: t('views.tradingMandate.marketOrder'), value: TradingOrderTypeEnum.MARKET_ORDER },
                        { title: t('views.tradingMandate.limitOrder'), value: TradingOrderTypeEnum.LIMIT_ORDER }
                      ]"
                      :label="t('views.tradingMandate.orderType')"
                      base-color="grey-lighten-1"
                      class="text-black"
                      hide-details
                    />
                  </v-col>

                  <!-- Limit Price (shown only for limit orders) -->
                  <v-col v-if="orderForm.orderType === TradingOrderTypeEnum.LIMIT_ORDER" cols="12" md="auto" style="width: 45%;">
                    <v-text-field
                      v-model="orderForm.limitPrice"
                      :disabled="loading"
                      :label="orderForm.toAsset === ''
                        ? t('views.tradingMandate.limitPrice')
                        : orderForm.toAsset === 'EURC'
                          ? t('views.tradingMandate.limitSellPrice')
                          : t('views.tradingMandate.limitBuyPrice')"
                      :rules="orderLimitPriceRules"
                      base-color="grey-lighten-1"
                      class="text-black"
                      hide-details="auto"
                      type="number"
                    />
                  </v-col>
                </v-row>

                <v-row align="center" class="mt-4" justify="space-between">
                  <!-- FROM field -->
                  <v-col cols="12" md="auto" style="width: 45%;">
                    <v-alert
                      color="grey-lighten-3"
                      variant="outlined"
                    >
                      <div class="d-flex justify-space-between">
                        <div class="text-black">
                          {{ t("views.tradingMandate.from") }}
                          <span class="text-grey-lighten-1 text-caption">
                            ({{ t("views.tradingMandate.requested") }})
                          </span>
                        </div>
                        <v-btn
                          :text="orderForm.fromAsset == ''
                            ? t('views.tradingMandate.selectAsset')
                            : orderForm.fromAsset"
                          :color="orderForm.fromAsset == ''
                            ? 'primary'
                            : 'secondary'"
                          size="small"
                          variant="flat"
                          @click="selectAsset(true)"
                        />
                      </div>
                      <div class="d-flex justify-space-between">
                        <v-text-field
                          v-model="orderForm.amount"
                          :disabled="loading"
                          :rules="orderAmountRules"
                          class="text-black"
                          hide-details="auto"
                          placeholder="0"
                          variant="plain"
                        />
                        <div v-if="orderForm.fromAsset != ''" class="mt-3 d-flex text-caption">
                          <span class="text-grey-darken-1">
                            {{ t("views.tradingMandate.availableAmount") }}:
                            <strong>
                              {{ getAmountAvailableByAsset(orderForm.fromAsset) }} €
                            </strong>
                          </span>
                        </div>
                      </div>
                    </v-alert>
                  </v-col>

                  <!-- Swap Icon -->
                  <v-col cols="12" md="1">
                    <div class="d-flex align-center justify-center">
                      <v-icon
                        color="primary"
                        :icon="smAndDown ? 'PhArrowsDownUp' : 'PhArrowsLeftRight'"
                        @click="swapAssets"
                      />
                    </div>
                  </v-col>

                  <!-- TO field -->
                  <v-col cols="12" md="auto" style="width: 45%;">
                    <v-alert
                      color="grey-lighten-3"
                      variant="outlined"
                    >
                      <div class="d-flex justify-space-between">
                        <div class="text-black">
                          {{ t("views.tradingMandate.to") }}
                          <span class="text-grey-lighten-1 text-caption">
                            ({{ t("views.tradingMandate.offered") }})
                          </span>
                        </div>
                        <v-btn
                          :text="orderForm.toAsset == ''
                            ? t('views.tradingMandate.selectAsset')
                            : orderForm.toAsset"
                          :color="orderForm.toAsset == ''
                            ? 'primary'
                            : 'secondary'"
                          size="small"
                          variant="flat"
                          @click="selectAsset(false)"
                        />
                      </div>
                      <div class="mt-3 d-flex text-caption">
                        <span v-if="orderForm.toAsset" class="text-grey-darken-1">
                          {{ t("views.tradingMandate.currentAmount") }}:
                          <strong>
                            {{ getAmountAvailableByAsset(orderForm.toAsset) }} €
                          </strong>
                        </span>
                        <span v-else>.</span>
                      </div>
                    </v-alert>
                  </v-col>

                  <!-- Submit Button -->
                  <v-col cols="12">
                    <v-btn
                      :disabled="loading || !orderFormValid || !orderForm.fromAsset || !orderForm.toAsset
                        || (orderForm.orderType === TradingOrderTypeEnum.LIMIT_ORDER && !orderForm.limitPrice)"
                      :loading="loading"
                      :text="t('views.tradingMandate.buttons.placeOrder')"
                      class="mb-4"
                      color="primary"
                      size="small"
                      type="submit"
                      variant="flat"
                    />
                  </v-col>
                </v-row>
              </v-form>
            </v-alert>
          </v-col>
        </v-row>
      </v-stepper-window-item>

      <!-- Step 2: Transaction history -->
      <v-stepper-window-item :value="2">
        <v-row>
          <v-col>
            <v-data-table-server
              :headers="headers"
              :items="ordersHistory"
              :items-length="total"
              :items-per-page="limit"
              :loading="loading"
              :no-data-text="t('common.noResult')"
              :page="page"
              :sort-by="[{ key: sort, order: order }]"
              hover
              must-sort
              @update:page="page = $event"
              @update:items-per-page="limit = $event"
              @update:sort-by="onSort"
            >
              <template #loading>
                <v-skeleton-loader type="table-row@4" />
              </template>
              <template #[`item.assetId`]="{ item }">
                {{ formatItemValue(item.assetName) }}
              </template>
              <template #[`item.side`]="{ item }">
                {{ t("views.tradingMandate.table.side." + item.side) }}
              </template>
              <template #[`item.subType`]="{ item }">
                {{ t("views.tradingMandate.table.type." + item.type) }}
              </template>
              <template #[`item.total`]="{ item }">
                {{ formatCurrency(Number(item.total) * 100) }}
              </template>
              <template #[`item.requestedPrice`]="{ item }">
                {{ formatCurrency(item.requestedPrice ? Number(item.requestedPrice) * 100 : null) }}
              </template>
              <template #[`item.status`]="{ item }">
                {{ t("views.tradingMandate.table.status." + item.status) }}
              </template>
              <template #[`item.time`]="{ item }">
                {{ formatDate(item.time) }}
              </template>
              <template #[`item.closingTime`]="{ item }">
                {{ formatDate(item.closingTime) }}
              </template>
              <template #[`item.action`]="{ item }">
                <v-btn
                  v-if="hasPermission(['TRADE_MANDATE']) && isOrderOngoing(item.status)"
                  :text="t('views.tradingMandate.buttons.cancelOrder')"
                  color="error"
                  size="small"
                  variant="flat"
                  @click="cancelOrder(item)"
                />
              </template>
              <template #bottom>
                <v-pagination
                  v-model="page"
                  :length="maxPages"
                  :tile="true"
                  density="compact"
                  total-visible="4"
                />
              </template>
            </v-data-table-server>
          </v-col>
        </v-row>
      </v-stepper-window-item>
    </v-stepper-window>
  </v-stepper>
</template>
